import { FolderShare, HydratedFolderShare } from "@zapier/client";
import { Key } from "@zapier/design-system";
import { useMemo, useState } from "react";
import { createFolderShareFromId } from "../createFolderShareFromId";
import { useHydratePendingShares } from "./useHydratePendingShares";

function filterAllMemberAndTeamShares(shares: HydratedFolderShare[]) {
  return shares.filter(
    ({ shareType }) => shareType !== "owner" && shareType !== "account",
  );
}

function findIdIndex(id: Key, shares: FolderShare[]) {
  return shares.findIndex(
    ({ id: previousId }) => String(previousId) === String(id),
  );
}

function findShareIdIndex(toAddShareId: Key, shares: FolderShare[]) {
  return shares.findIndex(
    ({ shareId }) => String(shareId) === String(toAddShareId),
  );
}

function idIsAccount(id: Key | null) {
  return String(id).startsWith("account_");
}

function shareTypeIsAccount({ shareType }: FolderShare) {
  return shareType === "account";
}

function sliceIndex(index: number, array: FolderShare[]) {
  return [...array.slice(0, index), ...array.slice(index + 1)];
}

/** Helper hook that handles state for displaying added and removed shares
 * that are pending to be added or removed from a folder.
 */

export function usePendingSharesState(shares: HydratedFolderShare[]) {
  // Folder shares that will be added on save
  const [rawToAdd, setToAdd] = useState<FolderShare[]>([]);
  // Folder shares that will be removed on save
  const [toDelete, setToDelete] = useState<FolderShare[]>([]);

  // Hydrate the members and teams that are pending to be added
  // so they can be previewed in the list
  const toAdd = useHydratePendingShares(rawToAdd);

  // Fired when new member is added to the list
  const onAdd = (id: Key | null) => {
    // if the id is "Everyone", set the toAdd to the account and remove all other shares
    if (idIsAccount(id)) {
      setToAdd([createFolderShareFromId(String(id))]);
      setToDelete(filterAllMemberAndTeamShares(shares));
      return;
    }

    // Add the share to the toAdd list
    setToAdd((previous) => {
      // Make sure it's not already in the toAdd list
      if (id && findIdIndex(id, previous) === -1) {
        // Build the share object and add it to the list
        const newFolderShare = createFolderShareFromId(String(id));
        const newToAdd = [...previous, newFolderShare];

        // Check to see if we need to remove the "all" share from:
        const pendingAllIndex = newToAdd.findIndex((nta) =>
          shareTypeIsAccount(nta),
        ); // current pending list
        const currentAllIndex = shares.findIndex((nta) =>
          shareTypeIsAccount(nta),
        ); // current share list
        const needToRemoveAll =
          pendingAllIndex !== -1 || currentAllIndex !== -1;

        // Only continue if we need to remove the "all" share
        if (needToRemoveAll) {
          // If the "all" share is in the pending list, remove it
          if (pendingAllIndex !== -1) {
            setToDelete((previousDelete) => [
              ...previousDelete,
              newToAdd[pendingAllIndex],
            ]);
            return sliceIndex(pendingAllIndex, newToAdd);
          }

          // Else, the "all" share was in the current saved list.
          // Move it to pending deletes
          setToDelete((previousDelete) => [
            ...previousDelete,
            shares[currentAllIndex],
          ]);
        }

        return newToAdd;
      }
      return previous;
    });

    // Remove the share from the toDelete list if it exists
    if (id) {
      setToDelete((previous) => {
        const index = findIdIndex(id, previous);
        // Remove if exists
        if (index !== -1) sliceIndex(index, previous);
        return previous;
      });
    }
  };

  // Fired when a member is removed from the list
  const onRemove = (id: Key) => {
    // Add the share to the toDelete list if it doesn't exist
    setToDelete((previous) => {
      if (findIdIndex(id, previous) === -1) {
        return [...previous, createFolderShareFromId(String(id))];
      }
      return previous;
    });
    // Remove share from toAdd list if it exists
    setToAdd((previous) => {
      const index = findIdIndex(id, previous);
      if (index !== -1) {
        return sliceIndex(index, previous);
      }
      return previous;
    });
  };

  // Takes the list of current shares, adds pending shares, and removes pending deletes
  const filteredShares = useMemo(() => {
    const filtered = [...shares];

    // Add all pending shares that aren't already in the list
    toAdd.forEach((shareToAdd) => {
      if (findShareIdIndex(shareToAdd.shareId, filtered) === -1) {
        filtered.push(shareToAdd);
      }
    });

    // Remove all pending deletes from the list
    return filtered.filter((share) => {
      return findShareIdIndex(share.shareId, toDelete) === -1;
    });
  }, [shares, toDelete, toAdd]);

  return {
    onAdd,
    onRemove,
    toAdd,
    toDelete,
    shares: filteredShares,
  };
}
